'use client';

import Link from 'next/link';
import { useTranslation } from '@/lib/i18n/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Logo } from '@/components/Logo';

export function Landing({ onPlay }: { onPlay: () => void }) {
  const { t } = useTranslation();

  const steps = [
    { n: '01', title: t('step1Title'), text: t('step1Text') },
    { n: '02', title: t('step2Title'), text: t('step2Text') },
    { n: '03', title: t('step3Title'), text: t('step3Text') },
  ];

  return (
    <div className="mx-auto flex h-dvh max-w-5xl flex-col overflow-hidden px-4">
      <header className="flex shrink-0 items-center justify-between py-4">
        <Logo className="text-xl" />
        <LanguageSelector />
      </header>

      <main className="flex min-h-0 flex-1 flex-col justify-center gap-[4vh]">
        <div className="relative">
          <span
            aria-hidden
            className="anim-pop absolute -top-2 right-0 hidden rotate-[8deg] rounded-lg border-2 border-dashed border-accent px-4 py-2 font-mono text-2xl font-bold text-accent sm:block"
            style={{ animationDelay: '300ms' }}
          >
            $ ???
          </span>

          <p className="anim-rise inline-block rounded-full border border-line bg-surface px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-muted">
            {t('landingEyebrow')}
          </p>

          <h1
            className="anim-rise mt-[2.5vh] font-display text-[clamp(2.5rem,11vw,6rem)] font-extrabold uppercase leading-[0.92] tracking-tight"
            style={{ animationDelay: '80ms' }}
          >
            {t('landingTitle1')}
            <br />
            <span className="text-accent">{t('landingTitle2')}</span>
          </h1>

          <p
            className="anim-rise mt-[2.5vh] max-w-xl text-base leading-relaxed text-muted sm:text-lg"
            style={{ animationDelay: '160ms' }}
          >
            {t('landingSubtitle')}
          </p>

          <button
            onClick={onPlay}
            className="btn-primary anim-rise mt-[3vh] px-10 py-3.5 font-display text-xl uppercase tracking-wide"
            style={{ animationDelay: '240ms' }}
          >
            {t('play')} →
          </button>
        </div>

        <ol className="grid grid-cols-3 gap-2 sm:gap-3">
          {steps.map((s, i) => (
            <li key={s.n} className="card anim-rise p-3 sm:p-4" style={{ animationDelay: `${320 + i * 80}ms` }}>
              <span className="font-mono text-xs font-bold text-accent">{s.n}</span>
              <h2 className="mt-1 font-display text-sm font-extrabold leading-tight sm:text-base">{s.title}</h2>
              <p className="mt-1 hidden text-xs text-muted sm:block">{s.text}</p>
            </li>
          ))}
        </ol>
      </main>

      <footer className="shrink-0 py-3 text-center">
        <Link href="/legal" className="text-[11px] text-muted transition-colors hover:text-fg">
          {t('legalNotice')}
        </Link>
      </footer>
    </div>
  );
}
